import { signal } from "@preact/signals-react";

export type HistoryManagerParams = Record<string, string>;

export type ManagedHistoryEntry<
  Kind extends string = string,
  Name extends string = string,
> = {
  entryId: string;
  href: string;
  instanceId?: string;
  kind: Kind;
  name: Name;
  parent?: ManagedHistoryEntry;
  params: HistoryManagerParams;
  sessionId: string;
};

export type HistoryManagerUpdateSource =
  | "popstate"
  | "push"
  | "replace"
  | "sync";

type HistoryManagerStore = {
  activeEntry: ManagedHistoryEntry | null;
  revision: number;
  source: HistoryManagerUpdateSource;
};

export const HISTORY_MANAGER_STATE_KEY = "__focusHistory";

export const historyManagerSignal = signal<HistoryManagerStore>({
  activeEntry: null,
  revision: 0,
  source: "sync",
});

const historyManagerSessionId =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `history-manager-session-${Date.now()}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const normalizeParams = (value: unknown): HistoryManagerParams => {
  if (!isRecord(value)) {
    return {};
  }

  return Object.entries(value).reduce<HistoryManagerParams>(
    (params, [key, paramValue]) => {
      if (typeof paramValue === "string") {
        params[key] = paramValue;
      }

      return params;
    },
    {},
  );
};

const normalizeManagedHistoryEntry = (
  value: unknown,
  depth = 0,
): ManagedHistoryEntry | null => {
  if (!isRecord(value)) {
    return null;
  }

  const entryId = value.entryId;
  const href = value.href;
  const instanceId = value.instanceId;
  const kind = value.kind;
  const name = value.name;
  const sessionId = value.sessionId;

  if (
    typeof entryId !== "string" ||
    typeof href !== "string" ||
    (instanceId !== undefined && typeof instanceId !== "string") ||
    typeof kind !== "string" ||
    typeof name !== "string" ||
    typeof sessionId !== "string"
  ) {
    return null;
  }

  const parent =
    depth < 10 ? normalizeManagedHistoryEntry(value.parent, depth + 1) : null;

  return {
    entryId,
    href,
    instanceId,
    kind,
    name,
    ...(parent ? { parent } : {}),
    params: normalizeParams(value.params),
    sessionId,
  };
};

export const getHistoryManagerSessionId = () => historyManagerSessionId;

export const createHistoryEntryId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `history-entry-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const getCurrentHref = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return `${window.location.pathname}${window.location.search}`;
};

export const createManagedHistoryEntry = <
  Kind extends string,
  Name extends string,
>({
  entryId = createHistoryEntryId(),
  href = getCurrentHref(),
  instanceId,
  kind,
  name,
  params = {},
}: {
  entryId?: string;
  href?: string | null;
  instanceId?: string;
  kind: Kind;
  name: Name;
  params?: HistoryManagerParams;
}): ManagedHistoryEntry<Kind, Name> | null => {
  if (!href) {
    return null;
  }

  return {
    entryId,
    href,
    instanceId,
    kind,
    name,
    params,
    sessionId: historyManagerSessionId,
  };
};

export const mergeHistoryManagerState = ({
  entry,
  historyState,
  parentEntry = null,
}: {
  entry: ManagedHistoryEntry | null;
  historyState: unknown;
  parentEntry?: ManagedHistoryEntry | null;
}) => {
  if (entry) {
    const nextEntry =
      parentEntry && parentEntry.entryId !== entry.entryId
        ? {
            ...entry,
            parent: parentEntry,
          }
        : entry;

    return {
      ...(isRecord(historyState) ? historyState : {}),
      [HISTORY_MANAGER_STATE_KEY]: nextEntry,
    };
  }

  if (!isRecord(historyState)) {
    return historyState ?? null;
  }

  if (!(HISTORY_MANAGER_STATE_KEY in historyState)) {
    return historyState;
  }

  const nextState = { ...historyState };
  delete nextState[HISTORY_MANAGER_STATE_KEY];

  return Object.keys(nextState).length > 0 ? nextState : null;
};

export const parseManagedHistoryEntry = ({
  historyState,
  sessionId = historyManagerSessionId,
}: {
  historyState: unknown;
  sessionId?: string;
}) => {
  if (!isRecord(historyState)) {
    return null;
  }

  const entry = normalizeManagedHistoryEntry(
    historyState[HISTORY_MANAGER_STATE_KEY],
  );

  if (!entry || entry.sessionId !== sessionId) {
    return null;
  }

  return entry;
};

export const findManagedHistoryEntry = ({
  historyState,
  predicate,
  sessionId = historyManagerSessionId,
}: {
  historyState: unknown;
  predicate: (entry: ManagedHistoryEntry) => boolean;
  sessionId?: string;
}) => {
  let entry = parseManagedHistoryEntry({
    historyState,
    sessionId,
  });

  while (entry) {
    if (entry.sessionId !== sessionId) {
      return null;
    }

    if (predicate(entry)) {
      return entry;
    }

    entry = entry.parent ?? null;
  }

  return null;
};

export const getCurrentManagedHistoryEntry = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return parseManagedHistoryEntry({
    historyState: window.history.state,
  });
};

export const syncHistoryManagerState = (
  source: HistoryManagerUpdateSource = "sync",
) => {
  historyManagerSignal.value = {
    activeEntry: getCurrentManagedHistoryEntry(),
    revision: historyManagerSignal.value.revision + 1,
    source,
  };
};

export const pushManagedHistoryEntry = (entry: ManagedHistoryEntry) => {
  if (typeof window === "undefined") {
    return;
  }

  window.history.pushState(
    mergeHistoryManagerState({
      entry,
      historyState: window.history.state,
      parentEntry: getCurrentManagedHistoryEntry(),
    }),
    "",
    entry.href,
  );
  syncHistoryManagerState("push");
};

export const replaceManagedHistoryEntry = (entry: ManagedHistoryEntry) => {
  if (typeof window === "undefined") {
    return;
  }

  const currentEntry = getCurrentManagedHistoryEntry();

  window.history.replaceState(
    mergeHistoryManagerState({
      entry,
      historyState: window.history.state,
      parentEntry: currentEntry?.parent ?? null,
    }),
    "",
    entry.href,
  );
  syncHistoryManagerState("replace");
};

export const pushHistoryUrl = (href: string) => {
  if (typeof window === "undefined") {
    return;
  }

  const currentHref = getCurrentHref();

  if (currentHref === href) {
    return;
  }

  window.history.pushState(
    mergeHistoryManagerState({
      entry: null,
      historyState: window.history.state,
    }),
    "",
    href,
  );
};

export const replaceHistoryUrl = (href: string) => {
  if (typeof window === "undefined") {
    return;
  }

  const currentHref = getCurrentHref();

  window.history.replaceState(
    mergeHistoryManagerState({
      entry: null,
      historyState: window.history.state,
    }),
    "",
    currentHref === href ? undefined : href,
  );
};
